import { useState, useEffect, useRef } from 'react';

export default function useIntersectionObserver(
  elementRef: React.RefObject<Element | null>,
  options?: IntersectionObserverInit
) {
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  // Callers routinely pass an inline options object (e.g. TrailPointItem's
  // `{ threshold: 0.75 }`), a new reference every render. Depending on
  // `options` directly re-created the observer every render — including
  // renders triggered by this hook's own setEntry — a perpetual
  // disconnect/observe loop that also drained battery. Depend on primitive,
  // content-based values instead so the effect only re-runs when the actual
  // config changes.
  const root = options?.root ?? null;
  const rootMargin = options?.rootMargin ?? '';
  const thresholdKey = JSON.stringify(options?.threshold ?? 0);

  useEffect(() => {
    const node = elementRef.current;
    if (!node) return;

    observer.current = new IntersectionObserver(([entry]) => setEntry(entry), {
      root,
      rootMargin,
      threshold: JSON.parse(thresholdKey),
    });

    observer.current.observe(node);

    return () => {
      observer.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, root, rootMargin, thresholdKey]);

  return entry;
}
